import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Paper,
    Switch,
    FormControlLabel,
    Button,
    Alert,
    CircularProgress,
    Divider,
} from '@mui/material';
import api from '../services/api';

const EmailPreferences = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [prefs, setPrefs] = useState({
        onboarding: false,
        progress: false,
        productEducation: false,
        weekly: false,
        promotional: false,
    });

    useEffect(() => {
        const token = searchParams.get('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        api.get('/email-preferences', { params: token ? { token } : {}, headers })
            .then(res => {
                setPrefs(res.data.emailPreferences || prefs);
                setEmail(res.data.email || '');
                setDisplayName(res.data.displayName || '');
                setLoading(false);
            })
            .catch(() => {
                setError('Unable to load email preferences. Please try logging in first.');
                setLoading(false);
            });
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const token = searchParams.get('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            await api.put('/email-preferences', { ...prefs, token }, { headers });
            setSuccess('Your email preferences have been updated.');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update preferences. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleUnsubscribeAll = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const token = searchParams.get('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            await api.put('/email-preferences', { unsubscribeAll: true, token }, { headers });
            setPrefs({
                onboarding: false,
                progress: false,
                productEducation: false,
                weekly: false,
                promotional: false,
            });
            setSuccess('You have been unsubscribed from all marketing emails. Transactional emails (password reset, billing) will still be sent.');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to unsubscribe. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    const categories = [
        { key: 'onboarding', label: 'Onboarding & Tips', desc: 'Welcome series, reading technique tips, and getting started guidance' },
        { key: 'progress', label: 'Progress Updates', desc: 'Milestone celebrations, badge notifications, streak reminders, and level-ups' },
        { key: 'productEducation', label: 'Product Education', desc: 'Feature highlights, new capabilities, and advanced reading techniques' },
        { key: 'weekly', label: 'Weekly Digest', desc: 'Weekly summary of your reading stats, progress, and recommendations' },
        { key: 'promotional', label: 'Promotional', desc: 'Special offers, discounts, and product announcements' },
    ];

    return (
        <Container maxWidth="sm" sx={{ mt: 4, mb: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                    Email Preferences
                </Typography>
                {displayName && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Account: {displayName} ({email})
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Choose which emails you'd like to receive from ReaderFLEX. Transactional emails (password reset, billing) will always be sent.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {categories.map((cat, idx) => (
                    <Box key={cat.key}>
                        {idx > 0 && <Divider sx={{ my: 1 }} />}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={prefs[cat.key]}
                                    onChange={(e) => setPrefs({ ...prefs, [cat.key]: e.target.checked })}
                                    name={cat.key}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {cat.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {cat.desc}
                                    </Typography>
                                </Box>
                            }
                            sx={{ alignItems: 'flex-start', py: 1 }}
                        />
                    </Box>
                ))}

                <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Preferences'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleUnsubscribeAll}
                        disabled={saving}
                    >
                        Unsubscribe from All
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default EmailPreferences;
